
import { useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '@/contexts/auth/UnifiedAuthProvider';
import { toast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

interface SessionTimeoutConfig {
  timeoutMinutes: number;
  warningMinutes: number;
  checkIntervalSeconds: number;
  onTimeout?: () => void;
  onWarning?: (minutesLeft: number) => void;
}

const DEFAULT_CONFIG: SessionTimeoutConfig = {
  timeoutMinutes: 120, // 2 hours
  warningMinutes: 10,  // Warn 10 minutes before timeout
  checkIntervalSeconds: 60, // Check every minute
};

export function useSessionTimeout(config: Partial<SessionTimeoutConfig> = {}) {
  const finalConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);
  const { user } = useUnifiedAuth();
  const navigate = useNavigate();
  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
  }, []);

  const handleTimeout = useCallback(() => {
    logger.warn('Session timeout occurred', {
      context: 'useSessionTimeout',
      action: 'sessionTimeout',
      metadata: { userId: user?.id }
    });
    
    toast({
      title: 'Session Expired',
      description: 'Your session has expired for security reasons. Please sign in again.',
      variant: 'destructive',
      duration: 5000
    });

    if (finalConfig.onTimeout) {
      finalConfig.onTimeout();
    } else {
      navigate('/auth');
    }
  }, [finalConfig, navigate]);

  const handleWarning = useCallback((minutesLeft: number) => {
    if (warningShownRef.current) return;
    
    warningShownRef.current = true;
    
    toast({
      title: 'Session Warning',
      description: `Your session will expire in ${minutesLeft} minutes due to inactivity.`,
      variant: 'default',
      duration: 8000
    });

    if (finalConfig.onWarning) {
      finalConfig.onWarning(minutesLeft);
    }
  }, [finalConfig]);

  const checkTimeout = useCallback(() => {
    if (!user) return;

    const now = Date.now();
    const timeSinceActivity = now - lastActivityRef.current;
    const timeoutMs = finalConfig.timeoutMinutes * 60 * 1000;
    const warningMs = finalConfig.warningMinutes * 60 * 1000;

    // Check if session should timeout
    if (timeSinceActivity >= timeoutMs) {
      handleTimeout();
      return;
    }

    // Check if warning should be shown
    const timeLeft = timeoutMs - timeSinceActivity;
    if (timeLeft <= warningMs && !warningShownRef.current) {
      const minutesLeft = Math.ceil(timeLeft / (60 * 1000));
      handleWarning(minutesLeft);
    }
  }, [user, finalConfig, handleTimeout, handleWarning]);

  // Set up activity tracking
  useEffect(() => {
    if (!user) return;

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    activityEvents.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [user, updateActivity]);

  // Set up timeout checking interval
  useEffect(() => {
    if (!user) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(checkTimeout, finalConfig.checkIntervalSeconds * 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, checkTimeout, finalConfig.checkIntervalSeconds]);

  // Initialize activity timestamp when user changes
  useEffect(() => {
    if (user) {
      updateActivity();
    }
  }, [user, updateActivity]);

  return {
    updateActivity,
    getRemainingTime: useCallback(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      const timeoutMs = finalConfig.timeoutMinutes * 60 * 1000;
      return Math.max(0, timeoutMs - timeSinceActivity);
    }, [finalConfig.timeoutMinutes]),
    isNearTimeout: useCallback(() => {
      const remaining = Date.now() - lastActivityRef.current;
      const warningMs = finalConfig.warningMinutes * 60 * 1000;
      const timeoutMs = finalConfig.timeoutMinutes * 60 * 1000;
      return (timeoutMs - remaining) <= warningMs;
    }, [finalConfig])
  };
}
